import { usePreserveSearchParamsNavigate } from "../../../../hooks/usePreserveSearchParamsNavigate.ts";
import { useUser } from "../../../hooks/useUser.ts";
import { useWorkspaceUuid } from "../../../hooks/useWorkspaceUuid.ts";
import { isUserAllowedTo } from "../../../utils/isUserAllowedTo.ts";
import { useAddAssetsToTag } from "../../hooks/useAddAssetsToTag.ts";
import { useBoardUuid } from "../../hooks/useBoardUuid.ts";
import { useDeleteAssetOnBoard } from "../../hooks/useDeleteAssetOnBoard.ts";
import { useFavoriteTagUuid } from "../../hooks/useFavoriteTagUuid.ts";
import { useRemoveAssetsFromTag } from "../../hooks/useRemoveAssetsFromTag.ts";
import { useUndeleteAssetOnBoard } from "../../hooks/useUndeleteAssetOnBoard.ts";
import { useUpdateContent } from "../../hooks/useUpdateContent.ts";
import { selectedGridAssetsUuidStore } from "../../stores/selectedGridAssetsUuidStore.ts";
import type { Asset } from "../../types.ts";
import { useDefaultEditTool } from "../../Workspaces/hooks/useDefaultTool.ts";
import { MemoAssetTile } from "./MemoAssetTile.tsx";
import { MemoDeletedAssetTile } from "./MemoDeletedAssetTile.tsx";
export const AssetTile = ({ asset }: { asset: Asset }) => {
  const boardUuid = useBoardUuid();
  const workspaceUuid = useWorkspaceUuid();
  const favoriteTagUuid = useFavoriteTagUuid();
  const navigate = usePreserveSearchParamsNavigate();

  const { mutate: undeleteAsset, isLoading: isUndeleteLoading } =
    useUndeleteAssetOnBoard({
      boardUuid,
    });

  const { mutate: deleteAsset, isLoading: isDeleteLoading } =
    useDeleteAssetOnBoard();
  const { mutate: addTag } = useAddAssetsToTag({
    assetUuids: [asset.uuid],
  });
  const { mutate: removeTag } = useRemoveAssetsFromTag({
    assetUuids: [asset.uuid],
  });
  const { user } = useUser();
  const isUserAllowedToCurate =
    user && isUserAllowedTo(user, "contents:curate");
  const { updateContent } = useUpdateContent();

  const isSelected = selectedGridAssetsUuidStore.useState((s) =>
    s.selectedGridAssetsUuid.some((uuid) => uuid === asset.uuid),
  );
  const isSelectionModeOn = selectedGridAssetsUuidStore.useState(
    (s) => s.selectedGridAssetsUuid.length > 0,
  );

  const addOrRemoveFavoriteTag = () => {
    if (asset.is_in_favorite && favoriteTagUuid) {
      removeTag(favoriteTagUuid);
    } else {
      addTag(favoriteTagUuid);
    }
  };

  const addOrRemoveFromInspirationLibrary = () => {
    if (asset.content.is_curated) {
      updateContent({
        contentUuid: asset.content.uuid,
        isCurated: false,
        linkedAssetUuid: asset.uuid,
      });
    } else {
      updateContent({
        contentUuid: asset.content.uuid,
        isCurated: true,
        linkedAssetUuid: asset.uuid,
      });
    }
  };

  const defaultEditTool = useDefaultEditTool();

  if (asset.deleted_at) {
    return (
      <MemoDeletedAssetTile
        asset={asset}
        undeleteAsset={undeleteAsset}
        isUndeleteLoading={isUndeleteLoading}
      />
    );
  }

  return (
    <MemoAssetTile
      asset={asset}
      workspaceUuid={workspaceUuid}
      navigate={navigate}
      boardUuid={boardUuid}
      addOrRemoveFavoriteTag={addOrRemoveFavoriteTag}
      isUserAllowedToCurate={isUserAllowedToCurate ?? false}
      addOrRemoveFromInspirationLibrary={addOrRemoveFromInspirationLibrary}
      isDeleteLoading={isDeleteLoading}
      deleteAsset={deleteAsset}
      isSelected={isSelected}
      isSelectionModeOn={isSelectionModeOn}
      defaultEditTool={defaultEditTool}
    />
  );
};
